<template>
  <div>
    <div>
      <div class="survey-table">
        <BaseModal v-model="wipeconfirm" hide-footer title="Delete survey permanently?">
          <p v-if="wipesurvey">
            WARNING! This operation will physically delete ALL information for this survey,
            including survey participants, answers to survey questions, and all reports.
          </p>
          <div v-if="wipesurveys.length > 0">
            WARNING! This operation will physically delete ALL information for the selected surveys,
            including survey participants, answers to survey questions, and all reports.
            <br />
            <br />
            This can take a long time depending on the amount of surveys selected. Please do not close the browser
            window.
          <ul class="mt-3">
            <li v-for="survey in wipesurveys" :key="survey.id" class="mb-1">{{ survey.name }} ({{ survey.id }})</li>
          </ul>
          </div>
          <p><strong>THIS OPERATION CANNOT BE REVERSED.</strong></p>
          <button class="btn btn-danger btn-block mt-3" @click="survey_deletefromdatabase(wipesurvey)">
            Delete
          </button>
          <button class="btn btn-secondary btn-block mt-2" @click="wipeconfirm = false; wipesurvey = false;">
            Cancel
          </button>
        </BaseModal>
        <BaseModal v-model="wipeprogress" hide-footer title="Permanently deleting survey(s)">
          <div class="row">
            <div class="col-12 text-center">
              <h5>Deleting survey(s)</h5>
            </div>
          </div>
          <div class="col-12 text-center">
            <div class="spinner-border text-danger" role="status" style="width: 4rem; height: 4rem;">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </BaseModal>
        <BaseModal id="deleteconfirm" hide-footer>
          D ya?
        </BaseModal>

        <div class="table-filter-area">
          <div class="row">
            <div class="col-md-8 pt-4">
              <div class="form-group">
                <input type="text" v-model="search" @input="handleSearch" class="form-control" placeholder="Search" />
              </div>
            </div>
            <div class="col-md-4 pt-4 text-right">
              <router-link :to="{ name: 'creategroupsurvey' }" class="btn btn-success">
                Create new survey
              </router-link>
            </div>
          </div>
        </div>

        <BaseTable
          id="surveys"
          ref="surveys"
          hover
          :items="filteredSurveys"
          :fields="fields"
          :busy="loading"
          :sort-by="orderBy"
          :sort-desc="orderDesc"
          :no-local-sorting="true"
          @sort-changed="surveys_sort"
          @row-clicked="showSurvey"
        >
          <template v-slot:cell(survey_name)="data">
            <div class="d-flex align-items-center">
              <div>
                {{ data.item.survey_name }}
              </div>
              <div v-if="data.item.is_template" class="ml-2 badge badge-info">
                Template
              </div>
            </div>
          </template>
          <template v-slot:cell(survey_language)="data">
            <span class="badge badge-secondary">{{ data.item.language_name }}</span>
          </template>
          <template v-slot:cell(survey_start_date)="data">
            {{ formatDate(data.item.survey_start_date) }}
          </template>
          <template v-slot:cell(survey_end_date)="data">
            {{ formatDate(data.item.survey_end_date) }}
          </template>
          <template v-slot:cell(actions)="data">
            <div class="d-flex justify-content-end">
              <BaseDropdown right class="ml-2">
                <template #button-content>
                  <i class="fa-solid fa-ellipsis"></i>
                </template>
                <a class="dropdown-item" @click.prevent="showSurvey(data.item)">View</a>
                <a class="dropdown-item" @click.prevent="editSurvey(data.item)" v-if="data.item.status === 0">Edit</a>
                <a class="dropdown-item" @click.prevent="previewSurvey(data.item)" v-if="data.item.status === 0">Preview</a>
                <a class="dropdown-item" @click.prevent="sendSurvey(data.item)" v-if="data.item.status === 0">Send</a>
                <a class="dropdown-item" @click.prevent="duplicateSurvey(data.item)" v-if="data.item.status === 0">Duplicate</a>
                <a class="dropdown-item" @click.prevent="deleteSurvey(data.item)">Delete</a>
              </BaseDropdown>
            </div>
          </template>
        </BaseTable>

        <div class="row">
          <div class="col-md-12 text-center mt-3">
            <div>Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage * perPage, totalSurveys) }} of {{ totalSurveys }}</div>
            <nav aria-label="Survey pagination">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                  <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                </li>
                <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: currentPage === page }">
                  <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import BaseTable from './base/BaseTable.vue'
import BaseDropdown from './base/BaseDropdown.vue'
import BaseModal from './base/BaseModal.vue'

const store = useStore()

// Reactive data
const surveys = ref([])
const loading = ref(false)
const currentPage = ref(1)
const perPage = ref(20)
const totalSurveys = ref(0)
const orderBy = ref('survey_start_date')
const orderDesc = ref(true)
const search = ref('')
const wipeconfirm = ref(false)
const wipesurvey = ref(false)
const wipesurveys = ref([])
const wipeprogress = ref(false)
const filteredSurveys = computed(() => {
  if (!search.value) return surveys.value
  return surveys.value.filter(survey => 
    survey.survey_name.toLowerCase().includes(search.value.toLowerCase()) ||
    (survey.language_name && survey.language_name.toLowerCase().includes(search.value.toLowerCase()))
  )
})
const fields = [
  { key: 'survey_name', label: 'Survey name', sortable: true },
  { key: 'survey_language', label: 'Language', sortable: false },
  { key: 'survey_start_date', label: 'Start date', sortable: true },
  { key: 'survey_end_date', label: 'End date', sortable: true },
  { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
]

// Computed properties
const totalPages = computed(() => {
  return Math.ceil(totalSurveys.value / perPage.value)
})

// Methods
const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const showSurvey = (survey) => {
  // Implementation depends on survey structure
  console.log('Show survey:', survey)
}

const editSurvey = (survey) => {
  // Implementation depends on survey structure
  console.log('Edit survey:', survey)
}

const previewSurvey = (survey) => {
  // Implementation depends on survey structure
  console.log('Preview survey:', survey)
}

const sendSurvey = (survey) => {
  // Implementation depends on survey structure
  console.log('Send survey:', survey)
}

const duplicateSurvey = (survey) => {
  // Implementation depends on survey structure
  console.log('Duplicate survey:', survey)
}

const deleteSurvey = (survey) => {
  wipesurvey.value = true
  wipesurveys.value = [survey]
  wipeconfirm.value = true
}

const handleSearch = () => {
  // Search will be handled by computed filter
}

const surveys_sort = ({ sortBy, sortDesc } = {}) => {
  if (sortBy) {
    orderBy.value = sortBy
  }
  if (typeof sortDesc === 'boolean') {
    orderDesc.value = sortDesc
  }
  getSurveys()
}

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  getSurveys()
}

const getSurveys = async () => {
  loading.value = true
  try {
    const filter = {}
    if (search.value) {
      filter.survey_name = {
        operator: 'LIKE',
        value: `%${search.value}%`
      }
    }
    
    // Adjusted for Vuex store usage and pagination
    await store.dispatch('groupsurveys_fetch', {
      filter,
      orderBy: orderBy.value,
      orderDesc: orderDesc.value,
      start: (currentPage.value - 1) * perPage.value,
      limit: perPage.value
    })
      .then((resp) => {
        surveys.value = resp.data.result
        totalSurveys.value = resp.data.total 
        loading.value = false
      })
  } catch (error) {
    console.error('Error fetching surveys:', error)
    loading.value = false
  }
}

const survey_deletefromdatabase = async (surveyToDelete) => {
  // Implementation would depend on actual delete logic
  console.log('Deleting survey:', surveyToDelete)
  wipeconfirm.value = false
  wipeprogress.value = true
}

// Lifecycle
onMounted(() => {
  getSurveys()
})

// Watchers
watch(search, () => {
  currentPage.value = 1
  getSurveys()
})
</script>

<style scoped>
.survey-table {
  position: relative;
}
</style>